import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { navigateTo } from '@/utils/navigation';
import type { PrestationCreatePayload, PrestationUpdatePayload, PrestationFilters } from '@/types/prestations';

const isTestEnv = (import.meta as any)?.env?.MODE === 'test';
// Default to relative path under Nginx proxy
const envBase = (import.meta as any)?.env?.VITE_API_BASE_URL as string | undefined;
let resolvedBase = envBase || '/api/v1';

// Resolve base URL for browser:
// - If VITE_API_BASE_URL is an absolute URL (http/https), KEEP IT as-is (useful in dev to target backend directly)
// - Otherwise (relative), anchor to current origin to preserve the port (e.g., :8014 via reverse proxy)
if (typeof window !== 'undefined') {
  try {
    if (/^https?:\/\//i.test(resolvedBase)) {
      // Keep absolute URL as-is
    } else {
      const pathOnly = resolvedBase;
      const normalizedPath = pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
      resolvedBase = `${window.location.origin}${normalizedPath}`;
    }
  } catch {
    // Fallback: keep previous resolvedBase
  }
}
export const API_BASE_URL = resolvedBase;

export const api = axios.create({
  baseURL: resolvedBase,
  // In tests, disable request timeout to avoid per-request timer handles keeping Node alive
  // For scraping operations, use 5 minutes timeout
  timeout: isTestEnv ? 0 : 300000,
});

// Export par défaut pour compatibilité avec les composants Achat
export default api;

// External public API client(s)
const openPlz = axios.create({
  baseURL: 'https://openplzapi.org',
  timeout: isTestEnv ? 0 : 10000,
});

// Dedicated client without interceptors for auth endpoints (refresh, login)
const authClient = axios.create({
  baseURL: resolvedBase,
  timeout: isTestEnv ? 0 : 10000,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    // Ensure headers object exists
    config.headers = (config.headers ?? {}) as any;
    if (token) {
      (config.headers as any).Authorization = `Bearer ${token}`;
    } else {
      delete (config.headers as any).Authorization;
    }
    return config;
  },
  (error: any) => Promise.reject(error)
);

// Response interceptor
// --- 401 -> refresh flow ---
let isRefreshing = false as boolean;
let refreshPromise: Promise<any> | null = null;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config as any;
    const status = error?.response?.status;

    // If not 401 or already retried, propagate
    if (status !== 401 || originalRequest?._retry) {
      return Promise.reject(error);
    }

    // Avoid refreshing on refresh endpoint or login endpoint
    const url: string = originalRequest?.url || '';
    if (url.includes('/auth/refresh') || url.includes('/auth/login')) {
      return Promise.reject(error);
    }

    const { refresh_token } = useAuthStore.getState();
    if (!refresh_token) {
      useAuthStore.getState().logout(navigateTo);
      return Promise.reject(error);
    }

    try {
      // Start or await a single refresh in-flight
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = authClient
          .post('/auth/refresh', { refresh_token })
          .then((res) => {
            const { access_token, refresh_token: newRefresh } = res.data || {};
            // update store
            // use set by mutating via a temporary setter; zustand not directly accessible here, so call logout/login-like set
            // We can't call login here; we update fields directly using set through a small helper:
            useAuthStore.setState({ token: access_token, refresh_token: newRefresh, isAuthenticated: true });
            return access_token;
          })
          .finally(() => {
            isRefreshing = false;
          });
      }

      const newAccessToken = await refreshPromise!;

      // Mark request as retried and update Authorization header
      originalRequest._retry = true;
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
      return api(originalRequest);
    } catch (e) {
      // Refresh failed: logout and redirect to login
      useAuthStore.getState().logout(navigateTo);
      return Promise.reject(e);
    } finally {
      // Clear promise to allow future refresh cycles
      if (!isRefreshing) refreshPromise = null;
    }
  }
);

// Combined API Service
export const apiService = {
  // Group Settings (branding, logo)
  getGroupSettings: async (): Promise<{
    id: number;
    admin_id: number;
    logo_url?: string | null;
    company_name?: string | null;
    currency?: string;
    primary_color?: string | null;
    secondary_color?: string | null;
    document_categories?: Array<{ value: string; label: string }> | null;
    client_notification_cc?: string[] | null;
    client_notification_bcc?: string[] | null;
    category_renames?: Record<string, string> | null;
    contact_roles?: Array<{ value: string; label: string }> | null;
    created_at: string;
    updated_at: string;
  }> => {
    const res = await api.get('/group-settings');
    return res.data;
  },
  updateGroupSettings: async (data: {
    company_name?: string | null;
    currency?: string | null;
    primary_color?: string | null;
    secondary_color?: string | null;
    document_categories?: Array<{ value: string; label: string }> | null;
    category_renames?: Record<string, string> | null;
    client_notification_cc?: string[] | null;
    contact_roles?: Array<{ value: string; label: string }> | null;
    contact_role_renames?: Record<string, string> | null;
    client_notification_bcc?: string[] | null;
  }): Promise<any> => {
    const res = await api.put('/group-settings', data);
    return res.data;
  },
  // BDC standalone endpoints (independent of Quotes)
  bdcListAll: async (): Promise<any[]> => {
    const res = await api.get('/bdc/');
    return Array.isArray(res.data) ? res.data : [];
  },
  bdcUpload: async (
    file: File,
    opts?: { onUploadProgress?: (percent: number) => void }
  ): Promise<any> => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const res = await api.post('/bdc/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (evt) => {
        try {
          if (!opts?.onUploadProgress) return;
          const total = (evt.total ?? 0) || (evt as any).currentTarget?.getResponseHeader?.('Content-Length') || 0;
          const loaded = evt.loaded ?? 0;
          const pct = total ? Math.round((loaded / total) * 100) : Math.min(99, Math.round((loaded / (1024 * 1024)) * 10));
          opts.onUploadProgress(pct);
        } catch { }
      },
    });
    try { (apiService as any)._docsCache?.clear?.(); } catch { }
    return res.data;
  },
  bdcListForQuote: async (quoteId: number): Promise<any[]> => {
    const res = await api.get(`/bdc/by-quote/${quoteId}`);
    return Array.isArray(res.data) ? res.data : [];
  },
  bdcLinkToQuote: async (documentId: number, quoteId: number): Promise<any> => {
    const res = await api.post(`/bdc/${documentId}/link`, undefined, { params: { quote_id: quoteId } });
    return res.data;
  },
  bdcUnlinkFromQuote: async (documentId: number, quoteId: number): Promise<any> => {
    const res = await api.post(`/bdc/${documentId}/unlink`, undefined, { params: { quote_id: quoteId } });
    return res.data;
  },
  bdcMakePrimaryForQuote: async (documentId: number, quoteId: number): Promise<any> => {
    const res = await api.post(`/bdc/${documentId}/make-primary`, undefined, { params: { quote_id: quoteId } });
    return res.data;
  },
  // Postal (Swiss only for now)
  postalListChNra: async (q: string, opts?: { limit?: number; offset?: number }): Promise<{ items: Array<{ nra: string; localities_count?: number | null }>; total: number }> => {
    const response = await api.get('/postal/CH/nra', { params: { q, limit: opts?.limit ?? 20, offset: opts?.offset ?? 0 } });
    return response.data;
  },
  postalListChLocalities: async (nra: string, opts?: { limit?: number; offset?: number }): Promise<{ items: Array<{ locality: string; canton?: string; commune?: string; lat?: number; lon?: number }> }> => {
    const response = await api.get(`/postal/CH/nra/${encodeURIComponent(nra)}/localities`, { params: { limit: opts?.limit ?? 50, offset: opts?.offset ?? 0 } });
    return response.data;
  },
  postalSearchChLocalities: async (q: string, opts?: { limit?: number; offset?: number }): Promise<{ items: Array<{ locality: string; canton?: string | null; nras: string[] }>; total: number }> => {
    const response = await api.get('/postal/CH/localities', { params: { q, limit: opts?.limit ?? 30, offset: opts?.offset ?? 0 } });
    return response.data;
  },
  // Postal via OpenPLZ (external public API)
  postalOpenPlzLocalitiesByPostalCode: async (
    postalCode: string,
    opts?: { limit?: number; offset?: number }
  ): Promise<{ items: Array<{ locality: string; canton?: string }> }> => {
    const res = await openPlz.get('/ch/Localities', { params: { postalCode } });
    const raw: any = res.data;
    // Normalize to { items: [{ locality, canton? }] }
    const list: any[] = Array.isArray(raw)
      ? raw
      : (Array.isArray(raw?.items) ? raw.items : (Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw?.Localities) ? raw.Localities : [])));
    const itemsAll = list
      .map((it: any) => {
        const locality = String(
          it?.locality ?? it?.name ?? it?.Name ?? it?.label ?? it?.Locality ?? it?.localite ?? ''
        ).trim();
        const cantonRaw = it?.canton ?? it?.Canton ?? it?.cantonCode ?? it?.state ?? it?.region ?? null;
        let canton: string | undefined;
        if (cantonRaw && typeof cantonRaw === 'object') {
          const cand = String(
            cantonRaw.code ?? cantonRaw.abbr ?? cantonRaw.name ?? cantonRaw.Name ?? ''
          ).trim();
          canton = cand || undefined;
        } else if (cantonRaw != null) {
          const cand = String(cantonRaw).trim();
          canton = cand || undefined;
        }
        return { locality, canton };
      })
      .filter((x: any) => x.locality.length > 0);
    const items = typeof opts?.limit === 'number' ? itemsAll.slice(0, opts.limit) : itemsAll;
    return { items };
  },

  // Backend proxy to OpenPLZ (preferred to avoid CORS and unify normalization)
  postalLocalitiesByPostalCodeViaProxy: async (
    postalCode: string,
    opts?: { limit?: number; offset?: number }
  ): Promise<{ items: Array<{ locality: string; canton?: string }> }> => {
    const response = await api.get('/postal/CH/openplz/localities', {
      params: {
        postalCode,
        limit: opts?.limit ?? 50,
        offset: opts?.offset ?? 0,
      },
    });
    // Response already normalized by backend: { items: [{ locality, canton? }] }
    return response.data;
  },
  postalAddCustomLocality: async (
    postalCode: string,
    locality: string,
    canton?: string
  ): Promise<{ item: { locality: string; canton?: string } }> => {
    const res = await api.post('/postal/CH/localities/custom', {
      postalCode,
      locality,
      ...(canton ? { canton } : {}),
    });
    return res.data;
  },
  // Auth
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', {
      email,
      password
    });
    return response.data;
  },
  getCurrentUser: async (): Promise<any> => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  register: async (userData: any) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  // Documents
  uploadDocument: async (opts: {
    file: Blob;
    filename: string;
    document_type?: string;
    category?: string;
    description?: string;
    tags?: string;
    deal_id?: number;
    contact_id?: number;
    company_id?: number;
    is_public?: boolean;
    send_by_email?: boolean;
    onUploadProgress?: (percent: number) => void;
  }) => {
    const fd = new FormData();
    fd.append('file', opts.file, opts.filename);
    if (opts.document_type) fd.append('document_type', opts.document_type);
    if (opts.category != null) fd.append('category', String(opts.category));
    if (opts.description != null) fd.append('description', String(opts.description));
    if (opts.tags != null) fd.append('tags', String(opts.tags));
    if (opts.deal_id != null) fd.append('deal_id', String(opts.deal_id));
    if (opts.contact_id != null) fd.append('contact_id', String(opts.contact_id));
    if (opts.company_id != null) fd.append('company_id', String(opts.company_id));
    if (typeof opts.is_public === 'boolean') fd.append('is_public', String(opts.is_public));
    if (typeof opts.send_by_email === 'boolean') fd.append('send_by_email', String(opts.send_by_email));
    const response = await api.post('/documents/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (evt) => {
        try {
          if (!opts.onUploadProgress) return;
          const total = (evt.total ?? 0) || (evt as any).currentTarget?.getResponseHeader?.('Content-Length') || 0;
          const loaded = evt.loaded ?? 0;
          const pct = total ? Math.round((loaded / total) * 100) : Math.min(99, Math.round((loaded / (1024 * 1024)) * 10));
          opts.onUploadProgress(pct);
        } catch { }
      },
    });
    // Invalidate cached document lists so UI refreshes instantly
    try { (apiService as any)._docsCache?.clear?.(); } catch { }
    return response.data;
  },
  // Manually invalidate the in-memory documents cache (used after mutations)
  invalidateDocumentsCache: (): void => {
    try { (apiService as any)._docsCache?.clear?.(); } catch { }
  },
  listDocuments: async (params?: {
    deal_id?: number;
    contact_id?: number;
    company_id?: number;
    document_type?: string;
    q?: string;
    is_public?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<any[]> => {
    // Simple in-memory cache to avoid duplicate concurrent calls across components
    // Keyed by params JSON
    const key = `docs:${JSON.stringify(params || {})}`;
    (apiService as any)._docsCache = (apiService as any)._docsCache || new Map<string, any[]>();
    const cache: Map<string, any[]> = (apiService as any)._docsCache;
    if (cache.has(key)) return cache.get(key)!;
    try {
      const res = await api.get('/documents/', { params });
      const data = Array.isArray(res.data) ? res.data : [];
      cache.set(key, data);
      return data;
    } catch (err: any) {
      // Some prod envs may route without trailing slash; try alternate path once
      try {
        const res2 = await api.get('/documents', { params });
        const data2 = Array.isArray(res2.data) ? res2.data : [];
        cache.set(key, data2);
        return data2;
      } catch (err2: any) {
        // As a mitigation for 500s in production, return empty array instead of throwing,
        // to avoid spamming the console with repeated errors from multiple components.
        console.warn('[apiService.listDocuments] failed', { params, err: err2?.response?.status || err2?.message || err2 });
        cache.set(key, []);
        return [];
      }
    }
  },
  deleteDocument: async (documentId: number): Promise<{ ok: boolean } | any> => {
    const res = await api.delete(`/documents/${documentId}`);
    // Invalidate cache on delete
    try { (apiService as any)._docsCache?.clear?.(); } catch { }
    return res.data;
  },
  updateDocument: async (documentId: number, data: Partial<{ name: string; document_type: string; category: string; description: string; tags: string; deal_id: number; contact_id: number; company_id: number; kind_id: number; is_public: boolean; send_by_email: boolean; }>): Promise<any> => {
    const res = await api.put(`/documents/${documentId}`, data);
    // Invalidate cache on update
    try { (apiService as any)._docsCache?.clear?.(); } catch { }
    return res.data;
  },
  // Clone documents from one quote to another and always prefix with the new quote reference
  cloneDocumentsForQuote: async (source_quote_id: number, target_quote_id: number): Promise<{ ok: boolean; cloned: number; deleted_old: number }> => {
    const res = await api.post('/documents/clone_for_quote', { source_quote_id, target_quote_id, cleanup_old: false });
    // Invalidate docs cache because new docs are created
    try { (apiService as any)._docsCache?.clear?.(); } catch { }
    return res.data;
  },
  downloadDocument: async (documentId: number): Promise<void> => {
    const response = await api.get(`/documents/${documentId}/download`, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    try {
      const dispo = (response.headers as any)['content-disposition'] as string | undefined;
      let filename = `document-${documentId}`;
      if (dispo) {
        const match = /filename\s*=\s*([^;]+)/i.exec(dispo);
        if (match && match[1]) filename = match[1].replace(/"/g, '').trim();
      }
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      window.URL.revokeObjectURL(url);
    }
  },
  getDocumentBlob: async (
    documentId: number
  ): Promise<{ blob: Blob; filename: string; contentType: string }> => {
    const response = await api.get(`/documents/${documentId}/download`, { responseType: 'blob' });
    const contentType = response.headers['content-type'] || 'application/octet-stream';
    const dispo = (response.headers as any)['content-disposition'] as string | undefined;
    let filename = `document-${documentId}`;
    if (dispo) {
      const match = /filename\s*=\s*([^;]+)/i.exec(dispo);
      if (match && match[1]) filename = match[1].replace(/"/g, '').trim();
    }
    const blob = new Blob([response.data], { type: contentType });
    return { blob, filename, contentType };
  },
  getQuotePdfBlob: async (
    quoteId: number
  ): Promise<{ blob: Blob; filename: string; contentType: string }> => {
    const response = await api.get(`/quotes/${quoteId}/pdf`, { responseType: 'blob' });
    const contentType = response.headers['content-type'] || 'application/pdf';
    const dispo = (response.headers as any)['content-disposition'] as string | undefined;
    let filename = `Devis-${quoteId}.pdf`;
    if (dispo) {
      const match = /filename\s*=\s*([^;]+)/i.exec(dispo);
      if (match && match[1]) filename = match[1].replace(/"/g, '').trim();
    }
    const blob = new Blob([response.data], { type: contentType });
    return { blob, filename, contentType };
  },
  transcribeDocument: async (documentId: number): Promise<{ text: string }> => {
    const response = await api.post(`/documents/${documentId}/transcribe`, undefined, { timeout: 120000 });
    return response.data;
  },
  // BDC (Bons de Commande) - Purchase Orders for Quotes
  bdcListByQuote: async (quoteId: number): Promise<any[]> => {
    const res = await api.get(`/quotes/${quoteId}/bdc`);
    return Array.isArray(res.data) ? res.data : [];
  },
  bdcUploadToQuote: async (
    quoteId: number,
    file: File,
    opts?: { name?: string; description?: string; onUploadProgress?: (percent: number) => void }
  ): Promise<any> => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    if (opts?.name) fd.append('name', opts.name);
    if (opts?.description) fd.append('description', opts.description);
    const res = await api.post(`/quotes/${quoteId}/bdc/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (evt) => {
        try {
          if (!opts?.onUploadProgress) return;
          const total = (evt.total ?? 0) || (evt as any).currentTarget?.getResponseHeader?.('Content-Length') || 0;
          const loaded = evt.loaded ?? 0;
          const pct = total ? Math.round((loaded / total) * 100) : Math.min(99, Math.round((loaded / (1024 * 1024)) * 10));
          opts.onUploadProgress(pct);
        } catch { }
      },
    });
    try { (apiService as any)._docsCache?.clear?.(); } catch { }
    return res.data;
  },
  bdcLinkExisting: async (quoteId: number, documentId: number): Promise<any> => {
    const res = await api.post(`/quotes/${quoteId}/bdc/link`, { document_id: documentId });
    try { (apiService as any)._docsCache?.clear?.(); } catch { }
    return res.data;
  },
  bdcDelete: async (quoteId: number, documentId: number): Promise<void> => {
    await api.delete(`/quotes/${quoteId}/bdc/${documentId}`);
    try { (apiService as any)._docsCache?.clear?.(); } catch { }
  },
  bdcMakePrimary: async (quoteId: number, documentId: number): Promise<{ status: string; primary_bdc_document_id: number }> => {
    const res = await api.post(`/quotes/${quoteId}/bdc/${documentId}/make-primary`);
    return res.data;
  },
  // Named Lists (for list-type section params)
  listNamedLists: async (): Promise<Array<{ id: number; key: string; name: string; description?: string; is_active: boolean }>> => {
    const res = await api.get('/lists');
    const items = Array.isArray(res.data) ? res.data : [];
    return items.map((x: any) => ({
      id: Number(x.id),
      key: String(x.key),
      name: String(x.name),
      description: typeof x.description === 'string' ? x.description : undefined,
      is_active: x.is_active !== false,
    }));
  },
  getNamedList: async (
    idOrKey: number | string
  ): Promise<{ id: number; key: string; name: string; items: Array<{ id: number; code: string; label: string }> }> => {
    const res = await api.get(`/lists/${encodeURIComponent(String(idOrKey))}`);
    const d = res.data || {};
    return {
      id: Number(d.id),
      key: String(d.key || ''),
      name: String(d.name || ''),
      items: Array.isArray(d.items) ? d.items.map((it: any) => ({ id: Number(it.id), code: String(it.code), label: String(it.label) })) : [],
    };
  },
  listNamedListItems: async (
    idOrKey: number | string
  ): Promise<Array<{ id: number; code: string; label: string; position?: number; is_active?: boolean }>> => {
    const res = await api.get(`/lists/${encodeURIComponent(String(idOrKey))}/items`);
    const items = Array.isArray(res.data) ? res.data : [];
    return items.map((it: any) => ({
      id: Number(it.id),
      code: String(it.code),
      label: String(it.label),
      position: typeof it.position === 'number' ? it.position : (isFinite(Number(it.position)) ? Number(it.position) : undefined),
      is_active: typeof it.is_active === 'boolean' ? it.is_active : undefined,
    }));
  },
  // Contacts
  getContacts: async (params?: any) => {
    const response = await api.get('/contacts/list', { params });
    return response.data;
  },
  getContact: async (id: number) => {
    const response = await api.get(`/contacts/${id}`);
    return response.data;
  },
  createContactQuote: async (contactId: number, payload?: { prestation_id?: number; title?: string }) => {
    const response = await api.post(`/contacts/${contactId}/quotes`, payload || {});
    return response.data;
  },
  getContactQuotesCount: async (contactId: number): Promise<{ count: number }> => {
    const response = await api.get(`/contacts/${contactId}/quotes/count`);
    return response.data;
  },
  createContact: async (data: any) => {
    const response = await api.post('/contacts/create', data);
    return response.data;
  },
  updateContact: async (id: number, data: any) => {
    const response = await api.put(`/contacts/${id}`, data);
    return response.data;
  },
  deleteContact: async (id: number) => {
    const response = await api.delete(`/contacts/${id}`);
    return response.data;
  },
  getContactDeals: async (id: number) => {
    const response = await api.get(`/contacts/${id}/deals`);
    return response.data;
  },
  getContactStats: async (id: number) => {
    const response = await api.get(`/contacts/${id}/stats`);
    return response.data;
  },
  getContactsAnalytics: async () => {
    const response = await api.get('/contacts/analytics');
    return response.data;
  },
  getContactsStats: async () => {
    const response = await api.get('/contacts/stats');
    return response.data;
  },

  // Salon exports
  exportSalonExcel: async (
    salonUuid: string
  ): Promise<{ blob: Blob; filename: string; contentType: string }> => {
    const response = await api.get(`/salons/${encodeURIComponent(salonUuid)}/export-excel`, { responseType: 'blob' });
    const contentType = response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const dispo = (response.headers as any)['content-disposition'] as string | undefined;
    let filename = `salon-${salonUuid.slice(0, 8)}.xlsx`;
    if (dispo) {
      const match = /filename\s*=\s*([^;]+)/i.exec(dispo);
      if (match && match[1]) filename = match[1].replace(/"/g, '').trim();
    }
    const blob = new Blob([response.data], { type: contentType });
    return { blob, filename, contentType };
  },
  deleteSalonResponse: async (salonUuid: string, responseId: number): Promise<{ success: boolean; message?: string } | any> => {
    const res = await api.delete(`/salons/${encodeURIComponent(salonUuid)}/responses/${responseId}`);
    return res.data;
  },
  exportSalonJson: async (
    salonId: number
  ): Promise<{ blob: Blob; filename: string; contentType: string }> => {
    const res = await api.get(`/salons/${salonId}/export-json`);
    const data = res.data as any;
    const name: string = String(data?.salon?.name || `salon_${salonId}`);
    const safe = name
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9-_]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase();
    const filename = `salon_template_${safe || salonId}.json`;
    const content = JSON.stringify(data, null, 2);
    const blob = new Blob([content], { type: 'application/json;charset=utf-8' });
    return { blob, filename, contentType: 'application/json;charset=utf-8' };
  },

  // Companies
  getCompanies: async (params?: any) => {
    const response = await api.get('/companies/list', { params });
    return response.data;
  },
  getCompany: async (id: number) => {
    const response = await api.get(`/companies/${id}`);
    return response.data;
  },
  createCompany: async (data: any) => {
    const response = await api.post('/companies/create', data);
    return response.data;
  },
  updateCompany: async (id: number, data: any) => {
    const response = await api.put(`/companies/${id}`, data);
    return response.data;
  },
  deleteCompany: async (id: number) => {
    const response = await api.delete(`/companies/${id}`);
    return response.data;
  },
  detachCompanyLinks: async (id: number) => {
    const response = await api.post(`/companies/${id}/detach-links`);
    return response.data;
  },
  getCompanyDeals: async (id: number) => {
    const response = await api.get(`/companies/${id}/deals`);
    return response.data;
  },
  getCompanyStats: async (id: number) => {
    const response = await api.get(`/companies/${id}/stats`);
    return response.data;
  },
  getCompaniesAnalytics: async () => {
    const response = await api.get('/companies/analytics');
    return response.data;
  },
  getCompaniesStats: async () => {
    const response = await api.get('/companies/stats');
    return response.data;
  },

  // Deals
  getDeals: async (params?: any) => {
    const response = await api.get('/deals', { params });
    return response.data;
  },
  getDeal: async (id: number) => {
    const response = await api.get(`/deals/${id}`);
    return response.data;
  },
  createDeal: async (data: any) => {
    const response = await api.post('/deals/create', data);
    return response.data;
  },
  updateDeal: async (id: number, data: any) => {
    const response = await api.put(`/deals/${id}`, data);
    return response.data;
  },
  deleteDeal: async (id: number) => {
    const response = await api.delete(`/deals/${id}`);
    return response.data;
  },

  // Deal Products (bulk associate)
  bulkAssociateDealProducts: async (
    data: { deal_id: number; associations: Array<{ product_id: number; quantity: number; unit_price?: number; notes?: string; discount_type?: 'percentage' | 'fixed_amount'; discount_value?: number }> }
  ) => {
    const response = await api.post(`/deal-products/bulk-associate`, data);
    return response.data;
  },

  // Communications
  getCommunications: async (params?: any) => {
    const response = await api.get('/communications', { params });
    return response.data;
  },
  getCommunication: async (id: number) => {
    const response = await api.get(`/communications/${id}`);
    return response.data;
  },
  createCommunication: async (data: any) => {
    const response = await api.post('/communications', data);
    return response.data;
  },
  updateCommunication: async (id: number, data: any) => {
    const response = await api.put(`/communications/${id}`, data);
    return response.data;
  },
  deleteCommunication: async (id: number) => {
    const response = await api.delete(`/communications/${id}`);
    return response.data;
  },

  // Activities (Notes)
  listDealNotes: async (
    dealId: number,
    params?: { skip?: number; limit?: number; days_back?: number }
  ) => {
    const response = await api.get('/activities/', {
      params: { deal_id: dealId, activity_type: 'NOTE', ...params },
    });
    return response.data;
  },
  createDealNote: async (
    payload: { deal_id: number; title: string; description?: string | null; document_ids?: number[] | null }
  ) => {
    const response = await api.post('/activities/', {
      activity_type: 'NOTE',
      ...payload,
    });
    return response.data;
  },
  updateDealNote: async (
    noteId: number,
    payload: { title?: string; description?: string | null; document_ids?: number[] | null }
  ) => {
    const response = await api.put(`/activities/${noteId}`, payload);
    return response.data;
  },
  deleteDealNote: async (noteId: number) => {
    const response = await api.delete(`/activities/${noteId}`);
    return response.data;
  },

  // Admin Purge (Super Admin only)
  purgeDeals: async (): Promise<{ success: boolean; deleted: Record<string, number> }> => {
    const response = await api.delete('/admin/purge/deals');
    return response.data;
  },
  purgeContacts: async (): Promise<{ success: boolean; deleted: Record<string, number> }> => {
    const response = await api.delete('/admin/purge/contacts');
    return response.data;
  },
  purgeCompanies: async (): Promise<{ success: boolean; deleted: Record<string, number> }> => {
    const response = await api.delete('/admin/purge/companies');
    return response.data;
  },
  purgeProducts: async (): Promise<{ success: boolean; deleted: Record<string, number> }> => {
    const response = await api.delete('/admin/purge/products');
    return response.data;
  },
  purgePipelines: async (): Promise<{ success: boolean; deleted: Record<string, number> }> => {
    const response = await api.delete('/admin/purge/pipelines');
    return response.data;
  },
  purgeTags: async (): Promise<{ success: boolean; deleted: Record<string, number> }> => {
    const response = await api.delete('/admin/purge/tags');
    return response.data;
  },

  // Product Categories
  listProductCategories: async (): Promise<import('@/types/productCategories').ProductCategory[]> => {
    const response = await api.get('/product-categories/');
    return response.data;
  },
  createProductCategory: async (
    payload: import('@/types/productCategories').ProductCategoryCreatePayload
  ): Promise<import('@/types/productCategories').ProductCategory> => {
    const response = await api.post('/product-categories/', payload);
    return response.data;
  },
  updateProductCategory: async (
    id: number,
    payload: import('@/types/productCategories').ProductCategoryUpdatePayload
  ): Promise<import('@/types/productCategories').ProductCategory> => {
    const response = await api.put(`/product-categories/${id}`, payload);
    return response.data;
  },
  deleteProductCategory: async (id: number): Promise<{ success: boolean } | any> => {
    const response = await api.delete(`/product-categories/${id}`);
    return response.data;
  },
  getProductCategoryAttributeSchema: async (id: number): Promise<{ category_id: number; keys: Array<{ key: string; label?: string; unit?: string; required?: boolean; description?: string; type?: string }> }> => {
    const response = await api.get(`/product-categories/${id}/attribute-schema`);
    const raw = response.data as any;
    // Normalize backend shape { fields: [{ name, label, type, required, description, options? }] }
    // into frontend shape { keys: [{ key, label, unit, required, description, type }] }
    const keys = Array.isArray(raw?.keys)
      ? raw.keys
      : Array.isArray(raw?.fields)
        ? raw.fields.map((f: any) => ({
          key: String(f.name ?? f.key ?? ''),
          label: f.label ?? f.name,
          unit: f.unit,
          required: Boolean(f.required),
          description: f.description,
          type: f.type,
        }))
        : [];
    return {
      category_id: typeof raw?.category_id === 'number' ? raw.category_id : id,
      keys,
    };
  },

  // Quotes
  listQuotes: async (params?: { skip?: number; limit?: number; status?: string; deal_id?: number; search?: string; customer_contact_id?: number }) => {
    const response = await api.get('/quotes/list', { params });
    return response.data;
  },
  getQuote: async (id: number) => {
    const response = await api.get(`/quotes/${id}`);
    try {
      const DEBUG_QUOTES = (import.meta as any)?.env?.VITE_DEBUG_QUOTES;
      if (DEBUG_QUOTES) {
        const items = Array.isArray(response.data?.items) ? response.data.items : [];
        const stats = {
          count: items.length,
          desc_present: items.filter((it: any) => (it?.description ?? '') !== '').length,
          unit_present: items.filter((it: any) => (it?.unit ?? '') !== '').length,
          price_present: items.filter((it: any) => it?.unit_price != null).length,
        };
        console.debug('[api.getQuote] id=%s stats=%o sample=%o', id, stats, items.slice(0, 3).map((it: any) => ({
          id: it.id,
          product_id: it.product_id,
          desc_len: (it?.description ?? '').length,
          unit: it.unit,
          unit_price: it.unit_price,
        })));
      }
    } catch { }
    return response.data;
  },
  createQuoteFromDeal: async (dealId: number) => {
    const response = await api.post(`/deals/${dealId}/quotes`);
    return response.data;
  },
  createQuote: async (data: any) => {
    const response = await api.post(`/quotes/create`, data);
    return response.data;
  },
  updateQuote: async (id: number, data: any) => {
    const response = await api.patch(`/quotes/${id}`, data);
    return response.data;
  },
  updateQuoteSettings: async (id: number, settings: any) => {
    const response = await api.patch(`/quotes/${id}/settings`, settings);
    return response.data;
  },
  pullDealInputsIntoSections: async (
    quoteId: number,
    opts?: { block_key?: string }
  ): Promise<any> => {
    const payload = opts && opts.block_key ? { block_key: opts.block_key } : {};
    try {
      const DEBUG_QUOTES = (import.meta as any)?.env?.VITE_DEBUG_QUOTES;
      if (DEBUG_QUOTES) console.debug('[api.pullDealInputsIntoSections] ->', { quoteId, payload });
    } catch { }
    try {
      const response = await api.post(`/quotes/${quoteId}/sections/pull-deal-inputs`, payload);
      try {
        const DEBUG_QUOTES = (import.meta as any)?.VITE_DEBUG_QUOTES || (import.meta as any)?.env?.VITE_DEBUG_QUOTES;
        if (DEBUG_QUOTES) console.debug('[api.pullDealInputsIntoSections] <- ok');
      } catch { }
      return response.data;
    } catch (e: any) {
      try {
        const DEBUG_QUOTES = (import.meta as any)?.env?.VITE_DEBUG_QUOTES;
        if (DEBUG_QUOTES) console.debug('[api.pullDealInputsIntoSections] <- error', {
          status: e?.response?.status,
          detail: e?.response?.data?.detail || e?.message,
        });
      } catch { }
      throw e;
    }
  },
  updateQuoteSectionInputsValues: async (
    quoteId: number,
    sectionKey: string,
    inputsValues: any
  ) => {
    const response = await api.patch(
      `/quotes/${quoteId}/sections/${encodeURIComponent(sectionKey)}/inputs_values`,
      { inputs_values: inputsValues }
    );
    return response.data;
  },
  checkQuoteCanDelete: async (id: number): Promise<{ can_delete: boolean; invoices_count?: number; message?: string }> => {
    try {
      const response = await api.get(`/quotes/${id}/can-delete`);
      return response.data;
    } catch (e: any) {
      // If endpoint doesn't exist (404), assume can delete (backward compat)
      if (e?.response?.status === 404) {
        return { can_delete: true };
      }
      throw e;
    }
  },
  deleteQuote: async (id: number) => {
    const response = await api.delete(`/quotes/${id}`);
    return response.data;
  },
  addQuoteItem: async (quoteId: number, item: any) => {
    try {
      const DEBUG_QUOTES = (import.meta as any)?.env?.VITE_DEBUG_QUOTES;
      if (DEBUG_QUOTES) console.debug('[api.addQuoteItem] ->', { quoteId, item });
    } catch { }
    const response = await api.post(`/quotes/${quoteId}/items`, item);
    try {
      const DEBUG_QUOTES = (import.meta as any)?.env?.VITE_DEBUG_QUOTES;
      if (DEBUG_QUOTES) console.debug('[api.addQuoteItem] <-', { returned_items: Array.isArray(response.data?.items) ? response.data.items.length : null });
    } catch { }
    return response.data;
  },
  updateQuoteItem: async (quoteId: number, itemId: number, data: any) => {
    try {
      const DEBUG_QUOTES = (import.meta as any)?.env?.VITE_DEBUG_QUOTES;
      if (DEBUG_QUOTES) console.debug('[api.updateQuoteItem] ->', { quoteId, itemId, data });
    } catch { }
    const response = await api.patch(`/quotes/${quoteId}/items/${itemId}`, data);
    try {
      const DEBUG_QUOTES = (import.meta as any)?.env?.VITE_DEBUG_QUOTES;
      if (DEBUG_QUOTES) console.debug('[api.updateQuoteItem] <-', { returned_items: Array.isArray(response.data?.items) ? response.data.items.length : null });
    } catch { }
    return response.data;
  },
  deleteQuoteItem: async (quoteId: number, itemId: number) => {
    try {
      const DEBUG_QUOTES = (import.meta as any)?.env?.VITE_DEBUG_QUOTES;
      if (DEBUG_QUOTES) console.debug('[api.deleteQuoteItem] ->', { quoteId, itemId });
    } catch { }
    const response = await api.delete(`/quotes/${quoteId}/items/${itemId}`);
    try {
      const DEBUG_QUOTES = (import.meta as any)?.env?.VITE_DEBUG_QUOTES;
      if (DEBUG_QUOTES) console.debug('[api.deleteQuoteItem] <-', { ok: true });
    } catch { }
    return response.data;
  },
  sendQuote: async (id: number) => {
    const response = await api.post(`/quotes/${id}/send`);
    return response.data;
  },
  sendQuoteDirect: async (
    id: number,
    opts?: { show_discounts?: boolean; apply_discounts?: boolean; default_tax_rate?: number }
  ) => {
    const response = await api.post(`/quotes/${id}/send_direct`, undefined, {
      params: {
        show_discounts: opts?.show_discounts,
        apply_discounts: opts?.apply_discounts,
        default_tax_rate: opts?.default_tax_rate,
      },
    });
    return response.data;
  },
  // Email defaults settings (team & user)
  getTeamQuoteEmailDefaults: async () => {
    const res = await api.get('/email/settings/quote-defaults');
    return res.data;
  },
  setTeamQuoteEmailDefaults: async (payload: {
    subject_template?: string | null;
    body_text_template?: string | null;
    body_html_template?: string | null;
    default_signature_id?: number | null;
    default_account_id?: number | null;
    default_template_id?: number | null;
  }) => {
    const res = await api.put('/email/settings/quote-defaults', payload);
    return res.data;
  },
  getUserQuoteEmailDefaults: async () => {
    const res = await api.get('/email/settings/quote-defaults/me');
    return res.data;
  },
  setUserQuoteEmailDefaults: async (payload: {
    subject_template?: string | null;
    body_text_template?: string | null;
    body_html_template?: string | null;
    default_signature_id?: number | null;
    default_account_id?: number | null;
    default_template_id?: number | null;
  }) => {
    const res = await api.put('/email/settings/quote-defaults/me', payload);
    return res.data;
  },
  renderQuoteEmailPreview: async (payload: any) => {
    const res = await api.post('/email/settings/quote-defaults/render', payload);
    return res.data as { subject: string; body_text: string; body_html: string };
  },
  sendTestQuoteEmail: async (payload: any) => {
    const res = await api.post('/email/settings/quote-defaults/send-test', payload);
    return res.data as { ok: boolean };
  },
  // Email Accounts (settings)
  emailListAccounts: async (): Promise<any[]> => {
    const res = await api.get('/email/accounts');
    return Array.isArray(res.data) ? res.data : [];
  },
  emailRevealSecret: async (accountId: number): Promise<{ has_secret: boolean; secret?: string | null }> => {
    const res = await api.get(`/email/accounts/${accountId}/secret`);
    return res.data;
  },
  emailUpdateAccount: async (
    accountId: number,
    payload: Partial<{ display_name: string; is_active: boolean; retention_days: number }>
  ): Promise<any> => {
    const res = await api.patch(`/email/accounts/${accountId}`, payload);
    return res.data;
  },
  emailUpdateAccountSmtp: async (
    accountId: number,
    payload: { host?: string; port?: number; secure?: boolean; starttls?: boolean; username?: string; password?: string; provider?: 'ovh' | 'gmail' | 'o365' }
  ): Promise<any> => {
    const res = await api.patch(`/email/accounts/${accountId}/smtp`, payload);
    return res.data;
  },
  emailUpdateAccountImap: async (
    accountId: number,
    payload: { host?: string; port?: number; ssl?: boolean; starttls?: boolean; username?: string; password?: string; provider?: 'ovh' | 'gmail' | 'o365' }
  ): Promise<any> => {
    const res = await api.patch(`/email/accounts/${accountId}/imap`, payload);
    return res.data;
  },
  // Email OAuth diagnostics
  emailOauthStatus: async (
    accountId: number
  ): Promise<{
    account_id: number;
    provider_raw: string;
    provider_normalized: string;
    auth_method: string;
    has_refresh_token: boolean;
    has_legacy_oauth_secret: boolean;
    can_decrypt_refresh_token: boolean;
    uses_per_account_oauth_client: boolean;
  }> => {
    const res = await api.get(`/email/accounts/${accountId}/oauth-status`);
    return res.data;
  },
  // Email test send with detailed logs
  emailTestSend: async (
    accountId: number
  ): Promise<{
    ok: boolean;
    logs: string[];
    message_id?: string;
    db_id?: number;
    error?: string;
  }> => {
    const res = await api.post(`/email/accounts/${accountId}/test/send`);
    return res.data;
  },
  emailPingSmtp: async (
    accountId: number,
    payload: { host: string; port: number; secure?: boolean; starttls?: boolean; username?: string; password?: string }
  ): Promise<{ ok: boolean; detail?: string }> => {
    const res = await api.post(`/email/accounts/${accountId}/smtp/ping`, payload);
    return res.data;
  },
  emailPingImap: async (
    accountId: number,
    payload: { host: string; port: number; ssl?: boolean; starttls?: boolean; username?: string; password?: string }
  ): Promise<{ ok: boolean; detail?: string }> => {
    const res = await api.post(`/email/accounts/${accountId}/imap/ping`, payload);
    return res.data;
  },
  // Email images (TinyMCE uploads)
  emailUploadImage: async (
    file: Blob,
    filename?: string
  ): Promise<{ location: string; filename: string }> => {
    const fd = new FormData();
    // Provide filename if available to help backend preserve extension
    if (filename) {
      fd.append('file', file, filename);
    } else {
      fd.append('file', file);
    }
    const res = await api.post('/email/upload-image', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data as { location: string; filename: string };
  },
  emailListImages: async (): Promise<{ items: { name: string; size: number; mtime: number; url: string }[] }> => {
    const res = await api.get('/email/files');
    return res.data;
  },
  emailDeleteImage: async (filename: string): Promise<{ ok: boolean }> => {
    const res = await api.delete(`/email/files/${encodeURIComponent(filename)}`);
    return res.data;
  },
  emailSmtpTest: async (accountId: number): Promise<{ ok: boolean } & Record<string, any>> => {
    const res = await api.post(`/email/accounts/${accountId}/smtp/test`);
    return res.data;
  },
  acceptQuote: async (id: number) => {
    const response = await api.post(`/quotes/${id}/accept`);
    return response.data;
  },
  declineQuote: async (id: number) => {
    const response = await api.post(`/quotes/${id}/decline`);
    return response.data;
  },
  expireQuote: async (id: number) => {
    const response = await api.post(`/quotes/${id}/expire`);
    return response.data;
  },
  exportQuotePdf: async (
    id: number,
    opts?: { show_discounts?: boolean; apply_discounts?: boolean; default_tax_rate?: number }
  ) => {
    const response = await api.get(`/quotes/${id}/pdf`, {
      responseType: 'blob',
      params: {
        show_discounts: opts?.show_discounts,
        apply_discounts: opts?.apply_discounts,
        default_tax_rate: opts?.default_tax_rate,
      },
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    try {
      // Try to derive filename from header
      const dispo = (response.headers as any)['content-disposition'] as string | undefined;
      let filename = `devis-${id}.pdf`;
      if (dispo) {
        const match = /filename\s*=\s*([^;]+)/i.exec(dispo);
        if (match && match[1]) {
          filename = match[1].replace(/\"/g, '').trim();
        }
      }
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      window.URL.revokeObjectURL(url);
    }
  },
  exportQuoteSectionDebugPdf: async (
    quoteId: number,
    sectionKey: string
  ) => {
    const response = await api.get(
      `/quotes/${quoteId}/sections/${encodeURIComponent(sectionKey)}/debug-pdf`,
      {
        responseType: 'blob',
      }
    );
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    try {
      let filename = `devis-${quoteId}-section-${sectionKey}-debug.pdf`;
      const dispo = (response.headers as any)['content-disposition'] as string | undefined;
      if (dispo) {
        const match = /filename\s*=\s*([^;]+)/i.exec(dispo);
        if (match && match[1]) {
          filename = match[1].replace(/\"/g, '').trim();
        }
      }
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      window.URL.revokeObjectURL(url);
    }
  },
  // Quote Sections Template: single-section export/import
  exportQuoteSectionTemplate: async (
    sectionIdOrKey: string
  ): Promise<{ schema_version: string; exported_at: string; section: any }> => {
    const response = await api.get(`/quotes/sections/${encodeURIComponent(sectionIdOrKey)}/export`);
    return response.data;
  },
  importQuoteSectionTemplate: async (
    payload: { schema_version: 'v1'; exported_at?: string; section: any },
    opts?: { import_mode?: 'as_new' | 'replace_by_key' | 'replace_by_id'; regenerate_ids?: boolean }
  ): Promise<{ status: string; mode: string; section: any }> => {
    const response = await api.post(`/quotes/sections/import`, {
      payload,
      import_mode: opts?.import_mode ?? 'replace_by_key',
      regenerate_ids: opts?.regenerate_ids ?? true,
    });
    return response.data;
  },
  // Preferences (Team-scoped)
  getTeamQuoteSectionsTemplate: async (): Promise<{ template: any[]; updated_at?: string | null }> => {
    const response = await api.get('/preferences/team/quote-sections-template');
    return response.data;
  },
  updateTeamQuoteSectionsTemplate: async (template: any[]): Promise<{ template: any[]; updated_at?: string | null }> => {
    const response = await api.put('/preferences/team/quote-sections-template', { template });
    return response.data;
  },

  // Prestations
  listPrestations: async (params?: PrestationFilters) => {
    const response = await api.get('/prestations/', { params });
    return response.data;
  },
  getPrestation: async (id: number) => {
    const response = await api.get(`/prestations/${id}`);
    return response.data;
  },
  createPrestation: async (data: PrestationCreatePayload) => {
    const response = await api.post('/prestations/', data);
    return response.data;
  },
  updatePrestation: async (id: number, data: PrestationUpdatePayload) => {
    const response = await api.put(`/prestations/${id}`, data);
    return response.data;
  },
  deletePrestation: async (id: number) => {
    const response = await api.delete(`/prestations/${id}`);
    return response.data;
  },

  // Deal-Prestations (many-to-many Deal <-> Prestation)
  listDealPrestationsByDeal: async (dealId: number) => {
    const response = await api.get(`/deal-prestations/deal/${dealId}`);
    return response.data;
  },
  getDealPrestation: async (associationId: number) => {
    const response = await api.get(`/deal-prestations/${associationId}`);
    return response.data;
  },
  associatePrestationToDeal: async (data: { deal_id: number; prestation_id: number; notes?: string }) => {
    const response = await api.post(`/deal-prestations/`, data);
    return response.data;
  },
  deleteDealPrestation: async (associationId: number) => {
    const response = await api.delete(`/deal-prestations/${associationId}`);
    return response.data;
  },
  getDealsForPrestation: async (prestationId: number) => {
    const response = await api.get(`/deal-prestations/prestation/${prestationId}/deals`);
    return response.data;
  },
  bulkAssociatePrestations: async (data: { deal_id: number; prestation_ids: number[] }) => {
    const response = await api.post(`/deal-prestations/bulk-associate`, data);
    return response.data;
  },

  // Email Accounts & OAuth
  emailCreateAccount: async (payload: {
    address: string;
    display_name?: string | null;
    provider: 'gmail' | 'outlook' | 'generic';
    auth_method?: 'password' | 'oauth2';
    access_level?: 'user_only' | 'group_shared';
  }): Promise<any> => {
    const response = await api.post('/email/accounts', {
      address: payload.address,
      display_name: payload.display_name ?? undefined,
      provider: payload.provider,
      auth_method: payload.auth_method ?? 'oauth2',
      access_level: payload.access_level ?? 'user_only',
    });
    return response.data;
  },
  emailOauthGoogleStart: async (account_id: number, loginHint?: string): Promise<{ auth_url: string }> => {
    const params: Record<string, any> = { account_id };
    if (loginHint && loginHint.trim()) params.login_hint = loginHint.trim();
    const response = await api.get('/email/oauth/google/start', { params });
    return response.data;
  },
  emailOauthMsStart: async (account_id: number, loginHint?: string): Promise<{ auth_url: string }> => {
    const params: Record<string, any> = { account_id };
    if (loginHint && loginHint.trim()) params.login_hint = loginHint.trim();
    const response = await api.get('/email/oauth/ms/start', { params });
    return response.data;
  },


  emailTestSmtp: async (
    account_id: number
  ): Promise<{ ok: boolean;[k: string]: any }> => {
    // Try preferred endpoint first, then fallback
    try {
      const res = await api.post(`/email/accounts/${account_id}/test/smtp`, undefined, { timeout: 45000 });
      return res.data;
    } catch (e1: any) {
      const res = await api.post(`/email/accounts/${account_id}/smtp/test`, undefined, { timeout: 45000 });
      return res.data;
    }
  },
  emailTestImap: async (account_id: number): Promise<{ ok: boolean;[k: string]: any }> => {
    const response = await api.post(`/email/accounts/${account_id}/test/imap`, undefined, { timeout: 45000 });
    return response.data;
  },
  emailTestOauth: async (account_id: number): Promise<{ success: boolean; provider: string; user_email?: string; user_name?: string; message?: string; error?: string; details?: string;[k: string]: any }> => {
    const response = await api.post(`/email/accounts/${account_id}/oauth/test`);
    return response.data;
  },
  emailSetAccountSignature: async (
    accountId: number,
    signatureId: number | null
  ): Promise<{ success: boolean; account_id: number; signature_id: number | null }> => {
    const response = await api.post(`/email/accounts/${accountId}/signature`, { signature_id: signatureId });
    return response.data;
  },
  emailGetAccountSignatures: async (): Promise<Record<number, number>> => {
    const fetchMap = async (path: string) => {
      const res = await api.get(path);
      return (res.data || {}) as Record<string, number>;
    };
    let raw: Record<string, number> = {};
    try {
      // Prefer alias to avoid any potential collision with /accounts/{account_id}
      raw = await fetchMap('/email/account-signatures');
    } catch (e1: any) {
      try {
        raw = await fetchMap('/email/accounts/signatures');
      } catch (e2: any) {
        throw e2 || e1;
      }
    }
    const mapped: Record<number, number> = {};
    for (const [k, v] of Object.entries(raw)) {
      const nk = Number(k);
      if (!Number.isNaN(nk) && typeof v === 'number') mapped[nk] = v;
    }
    return mapped;
  },
  emailSendSignatureTest: async (
    signatureId: number,
    payload: { account_id: number; test_email: string }
  ): Promise<{ success: boolean; message: string; signature_name: string }> => {
    const response = await api.post(`/email/signatures/${signatureId}/test`, payload);
    return response.data;
  },
  emailListSignatures: async (): Promise<any[]> => {
    const response = await api.get('/email/signatures');
    return response.data;
  },
  emailCreateSignature: async (payload: { name: string; content_html?: string | null; content_text?: string | null; is_default?: boolean }) => {
    const res = await api.post('/email/signatures', payload);
    return res.data;
  },
  emailUpdateSignature: async (
    id: number,
    payload: Partial<{ name: string; content_html?: string | null; content_text?: string | null; is_default?: boolean }>
  ) => {
    const res = await api.put(`/email/signatures/${id}`, payload);
    return res.data;
  },
  emailDeleteSignature: async (id: number) => {
    const res = await api.delete(`/email/signatures/${id}`);
    return res.data;
  },



  emailSend: async (payload: {
    account_id: number;
    to: string[];
    subject: string;
    body_html?: string;
    body_text?: string;
    cc?: string[];
    bcc?: string[];
    attachments?: Array<{ filename: string; content_base64: string; content_type?: string }>;
    quote_id?: number;
    signature_id?: number;
    document_ids?: number[];
  }): Promise<{ ok: boolean; id: number; message_id?: string }> => {
    const response = await api.post('/email/send', payload);
    return response.data;
  },

  emailDeleteAccount: async (account_id: number): Promise<void> => {
    await api.delete(`/email/accounts/${account_id}`);
  },

  createNamedList: async (payload: { key: string; name: string; description?: string | null; is_active?: boolean; items?: Array<{ code: string; label: string; position?: number; is_active?: boolean }> }) => {
    const response = await api.post('/lists', payload);
    return response.data as { id: number; key: string; name: string };
  },
  updateNamedList: async (id: number, payload: { name?: string; description?: string | null; is_active?: boolean }) => {
    const response = await api.patch(`/lists/${id}`, payload);
    return response.data as { id: number; key: string; name: string };
  },
  deleteNamedList: async (id: number) => {
    const response = await api.delete(`/lists/${id}`);
    return response.data as { success: boolean };
  },
  createNamedListItem: async (listId: number, payload: { code: string; label: string; position?: number; is_active?: boolean }) => {
    const response = await api.post(`/lists/${listId}/items`, payload);
    return response.data as { id: number; code: string; label: string };
  },
  updateNamedListItem: async (listId: number, itemId: number, payload: { code: string; label: string; position?: number; is_active?: boolean }) => {
    const response = await api.patch(`/lists/${listId}/items/${itemId}`, payload);
    return response.data as { id: number; code: string; label: string };
  },
  deleteNamedListItem: async (listId: number, itemId: number) => {
    const response = await api.delete(`/lists/${listId}/items/${itemId}`);
    return response.data as { success: boolean };
  },
  // Landing Analytics (Admin)
  landingGetAnalyticsAdmin: async (
    page_id: number,
    params?: { range?: '7d' | '30d' | '90d'; utm_source?: string | null; utm_medium?: string | null; utm_campaign?: string | null }
  ): Promise<{ range: string; visits: number; unique_visits: number; submissions: number; conversion_rate: number; utm_top_sources: Array<{ utm_source: string; count: number }>; utm_top_campaigns: Array<{ utm_campaign: string; count: number }>; since: string; contacts_created: number }> => {
    const response = await api.get(`/admin/landing/pages/${page_id}/analytics`, { params });
    return response.data;
  },
  landingGetAnalyticsSeriesAdmin: async (
    page_id: number,
    params?: { range?: '7d' | '30d' | '90d'; bucket?: 'day' | 'hour'; utm_source?: string | null; utm_medium?: string | null; utm_campaign?: string | null }
  ): Promise<{ range: string; bucket: 'day' | 'hour'; series: Array<{ t: string; visits: number; unique: number; submissions: number }> }> => {
    const response = await api.get(`/admin/landing/pages/${page_id}/analytics/series`, { params });
    return response.data;
  },
  // Inbox
  emailListMessages: async (
    account_id: number,
    opts?: { limit?: number; offset?: number; is_read?: boolean; is_archived?: boolean; tag?: string; exclude_tags?: string; direction?: 'inbound' | 'outbound'; search?: string }
  ) => {
    const response = await api.get('/email/messages', {
      params: {
        account_id,
        limit: opts?.limit,
        offset: opts?.offset,
        is_read: typeof opts?.is_read === 'boolean' ? opts?.is_read : undefined,
        is_archived: typeof opts?.is_archived === 'boolean' ? opts?.is_archived : undefined,
        tag: opts?.tag,
        direction: opts?.direction,
        exclude_tags: opts?.exclude_tags,
        search: opts?.search,
      },
    });
    return response.data as any[];
  },
  // Atomic multi-bucket listing
  emailListMessagesAll: async (
    account_id: number,
    opts?: { limit?: number; is_read?: boolean; search?: string }
  ): Promise<{ inbox: any[]; sent: any[]; archive: any[]; trash: any[]; spam: any[] }> => {
    const response = await api.get('/email/messages/all', {
      params: {
        account_id,
        limit: opts?.limit ?? 50,
        is_read: typeof opts?.is_read === 'boolean' ? opts?.is_read : undefined,
        search: opts?.search,
      },
    });
    const data = response.data || {};
    return {
      inbox: Array.isArray(data.inbox) ? data.inbox : [],
      sent: Array.isArray(data.sent) ? data.sent : [],
      archive: Array.isArray(data.archive) ? data.archive : [],
      trash: Array.isArray(data.trash) ? data.trash : [],
      spam: Array.isArray(data.spam) ? data.spam : [],
    };
  },
  emailMarkMessage: async (message_id: number, data: { is_read?: boolean; is_archived?: boolean }) => {
    const response = await api.post(`/email/messages/${message_id}/mark`, data);
    return response.data;
  },
  emailDeleteMessage: async (message_id: number): Promise<{ deleted: boolean }> => {
    const response = await api.delete(`/email/messages/${message_id}`);
    return response.data as { deleted: boolean };
  },
  emailSyncAccount: async (account_id: number, opts?: { limit?: number }) => {
    const response = await api.post(`/email/accounts/${account_id}/sync`, undefined, { params: { limit: opts?.limit }, timeout: 60000 });
    return response.data;
  },
  emailGetMessageDetail: async (message_id: number) => {
    const response = await api.get(`/email/messages/${message_id}`);
    return response.data as { message: any; attachments: any[] };
  },
  emailListMessageAttachments: async (message_id: number) => {
    const response = await api.get(`/email/messages/${message_id}/attachments`);
    return response.data as any[];
  },
  emailListAccountTags: async (account_id: number): Promise<{ tags: string[] }> => {
    const response = await api.get(`/email/tags`, { params: { account_id } });
    return response.data as { tags: string[] };
  },
  emailAddMessageTags: async (message_id: number, payload: { tags: string[] }) => {
    const response = await api.post(`/email/messages/${message_id}/tags/add`, payload);
    return response.data;
  },
  emailRemoveMessageTags: async (message_id: number, payload: { tags: string[] }) => {
    const response = await api.post(`/email/messages/${message_id}/tags/remove`, payload);
    return response.data;
  },
  // Threads
  emailListThreads: async (
    account_id: number,
    opts?: { limit?: number; offset?: number; is_archived?: boolean }
  ): Promise<Array<{ id: number; subject?: string; last_message_id: number; last_from?: string; last_date?: string; total: number; unread_count: number; has_attachments?: boolean }>> => {
    const response = await api.get('/email/threads', {
      params: {
        account_id,
        limit: opts?.limit ?? 50,
        offset: opts?.offset,
        is_archived: typeof opts?.is_archived === 'boolean' ? opts?.is_archived : undefined,
      },
    });
    return response.data;
  },
  emailGetThread: async (thread_id: number): Promise<{ id: number; subject?: string; messages: Array<{ message: any; attachments: any[] }> }> => {
    const response = await api.get(`/email/threads/${thread_id}`);
    return response.data;
  },
  emailMarkThread: async (thread_id: number, payload: { is_read?: boolean; is_archived?: boolean }): Promise<{ updated: number }> => {
    const response = await api.post(`/email/threads/${thread_id}/mark`, payload);
    return response.data as { updated: number };
  },
  emailBulkMarkMessages: async (ids: number[], payload: { is_read?: boolean; is_archived?: boolean }): Promise<{ updated: number }> => {
    const response = await api.post('/email/messages/bulk/mark', { ids, ...payload });
    return response.data as { updated: number };
  },
  emailBulkMarkThreads: async (ids: number[], payload: { is_read?: boolean; is_archived?: boolean }): Promise<{ updated: number }> => {
    const response = await api.post('/email/threads/bulk/mark', { ids, ...payload });
    return response.data as { updated: number };
  },
  emailMessagesCount: async (
    account_id: number,
    opts?: { is_read?: boolean; is_archived?: boolean; tag?: string; exclude_tags?: string; direction?: 'inbound' | 'outbound'; search?: string }
  ): Promise<{ count: number }> => {
    const response = await api.get('/email/messages/count', {
      params: {
        account_id,
        is_read: typeof opts?.is_read === 'boolean' ? opts?.is_read : undefined,
        is_archived: typeof opts?.is_archived === 'boolean' ? opts?.is_archived : undefined,
        tag: opts?.tag,
        direction: opts?.direction,
        exclude_tags: opts?.exclude_tags,
        search: opts?.search,
      },
    });
    return response.data as { count: number };
  },
  emailThreadsCount: async (
    account_id: number,
    opts?: { is_archived?: boolean }
  ): Promise<{ count: number }> => {
    const response = await api.get('/email/threads/count', {
      params: {
        account_id,
        is_archived: typeof opts?.is_archived === 'boolean' ? opts?.is_archived : undefined,
      },
    });
    return response.data as { count: number };
  },
  listEmailTemplates: async (): Promise<any[]> => {
    const response = await api.get('/email/templates');
    const data = response.data || {};
    return Array.isArray(data.items) ? data.items : [];
  },
  createEmailTemplate: async (payload: { name: string; subject_template: string; body_html_template?: string; body_text_template?: string; is_shared?: boolean; scope?: 'me' | 'team'; }) => {
    const response = await api.post('/email/templates', payload);
    return response.data;
  },
  updateEmailTemplate: async (id: number, payload: Partial<{ name: string; subject_template: string; body_html_template: string; body_text_template: string; is_shared: boolean; }>) => {
    const response = await api.patch(`/email/templates/${id}`, payload);
    return response.data;
  },
  deleteEmailTemplate: async (id: number) => {
    const response = await api.delete(`/email/templates/${id}`);
    return response.data;
  },
  listEmailSignatures: async () => {
    const response = await api.get('/email/signatures');
    return response.data as any[];
  },
  createEmailSignature: async (payload: { name: string; content_html?: string; content_text?: string; is_default?: boolean; }) => {
    const response = await api.post('/email/signatures', payload);
    return response.data;
  },
  updateEmailSignature: async (id: number, payload: Partial<{ name: string; content_html: string; content_text: string; is_default: boolean; }>) => {
    const response = await api.patch(`/email/signatures/${id}`, payload);
    return response.data;
  },
  deleteEmailSignature: async (id: number) => {
    const response = await api.delete(`/email/signatures/${id}`);
    return response.data;
  },
  // Drafts
  emailListDrafts: async (account_id?: number): Promise<any[]> => {
    const response = await api.get('/email/drafts', { params: { account_id } });
    return response.data;
  },
  emailCreateDraft: async (payload: {
    account_id: number;
    to_addresses?: string[];
    cc_addresses?: string[];
    bcc_addresses?: string[];
    subject?: string;
    text_body?: string;
    html_body?: string;
    attachments?: Array<{ filename: string; content_base64: string; content_type: string }>;
  }) => {
    const response = await api.post('/email/drafts', payload);
    return response.data;
  },
  emailUpdateDraft: async (
    draft_id: number,
    payload: Partial<{
      to_addresses: string[];
      cc_addresses: string[];
      bcc_addresses: string[];
      subject: string;
      text_body: string;
      html_body: string;
      attachments: Array<{ filename: string; content_base64: string; content_type: string }>;
    }>
  ) => {
    const response = await api.patch(`/email/drafts/${draft_id}`, payload);
    return response.data;
  },
  emailDeleteDraft: async (draft_id: number): Promise<{ deleted: boolean }> => {
    const response = await api.delete(`/email/drafts/${draft_id}`);
    return response.data;
  },

  // Landing Pages (Admin)
  landingListPages: async (): Promise<Array<{ id: number; slug: string; title: string; published: boolean }>> => {
    const response = await api.get('/landing/pages');
    return response.data;
  },
  landingCreatePage: async (payload: { slug: string; title: string; content?: any; theme?: any; published?: boolean }): Promise<any> => {
    const response = await api.post('/landing/pages', payload);
    return response.data;
  },
  landingUpdatePage: async (id: number, payload: Partial<{ slug: string; title: string; content: any; theme: any; published: boolean }>): Promise<any> => {
    const response = await api.put(`/landing/pages/${id}`, payload);
    return response.data;
  },
  landingGetPage: async (id: number): Promise<{ id: number; slug: string; title: string; published: boolean; content: any; theme: any }> => {
    const response = await api.get(`/landing/pages/${id}`);
    return response.data;
  },
  landingDeletePage: async (id: number): Promise<void> => {
    await api.delete(`/landing/pages/${id}`);
  },
  landingGetForm: async (pageId: number): Promise<any | null> => {
    const response = await api.get(`/landing/pages/${pageId}/form`);
    return response.data;
  },
  landingUpsertForm: async (pageId: number, payload: { schema: any; consent_text?: string | null; success_message?: string | null }): Promise<any> => {
    const response = await api.put(`/landing/pages/${pageId}/form`, payload);
    return response.data;
  },
  landingListDomains: async (pageId: number): Promise<Array<{ id: number; domain: string; status: string }>> => {
    const response = await api.get(`/landing/pages/${pageId}/domains`);
    return response.data;
  },
  landingAddDomain: async (pageId: number, domain: string): Promise<any> => {
    const response = await api.post(`/landing/pages/${pageId}/domains`, { domain });
    return response.data;
  },
  landingDeleteDomain: async (domainId: number): Promise<void> => {
    await api.delete(`/landing/domains/${domainId}`);
  },
  // Landing public helpers (rarely used from authenticated app; provided for tools)
  landingGetPublic: async (slug: string): Promise<any> => {
    const client = axios.create({ baseURL: `${window.location.origin}/api/public/landing-pages` });
    const res = await client.get(`/${encodeURIComponent(slug)}`);
    return res.data;
  },
  landingGetQrPngUrl: (slug: string): string => {
    return `${window.location.origin}/api/public/landing-pages/${encodeURIComponent(slug)}/qrcode`;
  },
  landingGetAnalytics: async (
    pageId: number,
    range: string = '30d',
    opts?: { utm_source?: string | null; utm_medium?: string | null; utm_campaign?: string | null }
  ): Promise<{ range: string; visits: number; unique_visits: number; submissions: number; conversion_rate: number; utm_top_sources: Array<{ utm_source: string; count: number }>; since: string; contacts_created?: number; }> => {
    const response = await api.get(`/landing/pages/${pageId}/analytics`, { params: { range, ...opts } });
    return response.data;
  },
  landingGetAnalyticsSeries: async (
    pageId: number,
    range: string = '30d',
    bucket: 'day' | 'hour' = 'day',
    opts?: { utm_source?: string | null; utm_medium?: string | null; utm_campaign?: string | null }
  ): Promise<{ range: string; bucket: 'day' | 'hour'; series: Array<{ t: string; visits: number; unique: number; submissions: number }> }> => {
    const response = await api.get(`/landing/pages/${pageId}/analytics/series`, { params: { range, bucket, ...opts } });
    return response.data;
  },
  landingGetAnalyticsHeatmap: async (
    pageId: number,
    range: string = '30d'
  ): Promise<{ range: string; grid: number[][] }> => {
    const response = await api.get(`/landing/pages/${pageId}/analytics/heatmap`, { params: { range } });
    return response.data;
  },

  // E-Sign (MVP)
  esignCreateEnvelope: async (payload: {
    title?: string | null;
    quote_id?: number | null;
    deal_id?: number | null;
    document_ids: number[];
    recipients: Array<{ full_name?: string | null; email: string; role?: 'signer' | 'approver' | 'cc'; order_index?: number; otp_channel?: 'none' | 'email' | 'sms' }>
    expires_at?: string | null; // ISO
  }): Promise<{ id: number; status: string; title?: string; quote_id?: number | null; deal_id?: number | null; created_at: string; updated_at: string; expires_at?: string | null }> => {
    const res = await api.post(`/esign/envelopes`, payload);
    return res.data;
  },
  esignGetEnvelope: async (envelope_id: number): Promise<{ id: number; status: string; title?: string; quote_id?: number | null; deal_id?: number | null; created_at: string; updated_at: string; expires_at?: string | null }> => {
    const res = await api.get(`/esign/envelopes/${envelope_id}`);
    return res.data;
  },
  esignSendEnvelope: async (envelope_id: number, req?: { mode?: 'notify_only' | 'send_emails' }): Promise<{ id: number; status: string; title?: string; quote_id?: number | null; deal_id?: number | null; created_at: string; updated_at: string; expires_at?: string | null }> => {
    const res = await api.post(`/esign/envelopes/${envelope_id}/send`, req || { mode: 'send_emails' });
    return res.data;
  },
  esignGetEnvelopeDetail: async (
    envelope_id: number
  ): Promise<{ envelope: { id: number; title?: string | null; status: string; deal_id?: number | null; quote_id?: number | null; expires_at?: string | null }; recipients: Array<{ id: number; full_name?: string | null; email?: string | null; status?: string | null; otp_channel?: string | null; verified_at?: string | null; last_otp_sent_at?: string | null }>; documents: Array<{ document_id: number }>; events: Array<{ id: number; type?: string | null; ts?: string | null; ip?: string | null }> }> => {
    const res = await api.get(`/esign/envelopes/${envelope_id}/detail`);
    return res.data;
  },
  esignResendInvitation: async (
    envelope_id: number,
    opts?: { recipient_id?: number }
  ): Promise<{ ok: boolean; sent: number }> => {
    const res = await api.post(`/esign/envelopes/${envelope_id}/resend`, undefined, { params: { recipient_id: opts?.recipient_id } });
    return res.data;
  },
  esignListQuoteEnvelopes: async (quote_id: number): Promise<{
    envelopes: Array<{
      id: number;
      title?: string;
      status: string;
      created_at?: string;
      expires_at?: string;
      document_count?: number;
      has_quote_pdf?: boolean;
      recipients: Array<{
        id: number;
        full_name?: string;
        email: string;
        status: string;
      }>;
    }>;
  }> => {
    const res = await api.get(`/esign/quotes/${quote_id}/envelopes`);
    return res.data;
  },
  esignDeleteEnvelope: async (envelope_id: number): Promise<{ ok: boolean; message: string }> => {
    const res = await api.delete(`/esign/envelopes/${envelope_id}`);
    return res.data;
  },
  esignGetSignedDocuments: async (envelope_id: number): Promise<{
    envelope_id: number;
    status: string;
    documents: Array<{
      id: number;
      name: string;
      category: string;
      mime_type: string;
      file_size: number;
      created_at?: string;
    }>;
  }> => {
    const res = await api.get(`/esign/envelopes/${envelope_id}/signed-documents`);
    return res.data;
  },

  // ===== Invoices =====
  listInvoices: async (params?: {
    status?: string;
    quote_id?: number;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<any[]> => {
    const res = await api.get('/invoices/', { params });
    return res.data;
  },
  getInvoice: async (invoiceId: number): Promise<any> => {
    const res = await api.get(`/invoices/${invoiceId}`);
    return res.data;
  },
  createInvoice: async (data: {
    quote_id: number;
    percentage: number;
    due_days?: number;
    notes?: string;
    payment_terms?: string;
  }): Promise<any> => {
    const res = await api.post('/invoices/', data);
    return res.data;
  },
  updateInvoice: async (invoiceId: number, data: {
    status?: string;
    notes?: string;
    payment_terms?: string;
    paid_date?: string;
  }): Promise<any> => {
    const res = await api.patch(`/invoices/${invoiceId}`, data);
    return res.data;
  },
  deleteInvoice: async (invoiceId: number): Promise<void> => {
    await api.delete(`/invoices/${invoiceId}`);
  },
  getInvoicePdfBlob: async (invoiceId: number): Promise<{ blob: Blob; filename: string; contentType: string }> => {
    const response = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
    const contentType = response.headers['content-type'] || 'application/pdf';
    const dispo = (response.headers as any)['content-disposition'] as string | undefined;
    let filename = `Invoice-${invoiceId}.pdf`;
    if (dispo) {
      const match = /filename\s*=\s*([^;]+)/i.exec(dispo);
      if (match && match[1]) filename = match[1].replace(/"/g, '').trim();
    }
    const blob = new Blob([response.data], { type: contentType });
    return { blob, filename, contentType };
  },
  sendInvoice: async (invoiceId: number): Promise<any> => {
    const res = await api.post(`/invoices/${invoiceId}/send`);
    return res.data;
  },
  markInvoicePaid: async (invoiceId: number): Promise<any> => {
    const res = await api.post(`/invoices/${invoiceId}/mark-paid`);
    return res.data;
  },

  // Document Kinds
  listDocumentKinds: async (params?: { is_active?: boolean; limit?: number; offset?: number }): Promise<Array<{
    id: number;
    code: string;
    label: string;
    is_active: boolean;
    group_admin_id?: number | null;
    created_by: number;
    created_at: string;
    updated_at: string;
  }>> => {
    const response = await api.get('/document-kinds', { params });
    return response.data;
  },
  createDocumentKind: async (payload: { code: string; label: string; is_active?: boolean }): Promise<{
    id: number;
    code: string;
    label: string;
    is_active: boolean;
    group_admin_id?: number | null;
    created_by: number;
    created_at: string;
    updated_at: string;
  }> => {
    const response = await api.post('/document-kinds', payload);
    return response.data;
  },
  updateDocumentKind: async (kindId: number, payload: { label?: string; is_active?: boolean }): Promise<{
    id: number;
    code: string;
    label: string;
    is_active: boolean;
    group_admin_id?: number | null;
    created_by: number;
    created_at: string;
    updated_at: string;
  }> => {
    const response = await api.patch(`/document-kinds/${kindId}`, payload);
    return response.data;
  },
  deleteDocumentKind: async (kindId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/document-kinds/${kindId}`);
    return response.data;

  },

  // Generic HTTP methods for flexible API calls
  get: async (url: string, config?: any): Promise<any> => {
    const response = await api.get(url, config);
    return response.data;
  },
  post: async (url: string, data?: any, config?: any): Promise<any> => {
    const response = await api.post(url, data, config);
    return response.data;
  },
  patch: async (url: string, data?: any, config?: any): Promise<any> => {
    const response = await api.patch(url, data, config);
    return response.data;
  },
  put: async (url: string, data?: any, config?: any): Promise<any> => {
    const response = await api.put(url, data, config);
    return response.data;
  },
  delete: async (url: string, config?: any): Promise<any> => {
    const response = await api.delete(url, config);
    return response.data;
  },
};
